import { useMemo, useState } from 'react';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import { domainOf } from '../../lib/url';
import type { CuratedFeedRead } from '../../types/api';
import { addFromCatalog, type FeedSourceCollection } from './newsApi';
import {
  followedCatalogIds,
  matchIndustryGroup,
  normaliseFeedUrl,
  useFeedCatalog,
} from './useFeedCatalog';

/** What the user's own trade is called, on the group that matches it. */
const OWN_INDUSTRY_TAG = 'Your industry';

interface NewsCatalogPanelProps {
  businessId: string;
  /** The one sources collection `/news` holds. Followed rows land in it. */
  collection: FeedSourceCollection;
  /** The industry the user typed at onboarding. Free text, matched loosely. */
  industry: string | null;
  /** Reported so the page knows the feed's inputs moved while the dialog was open. */
  onAdded: () => void;
}

/**
 * Choose from our list — the curated catalogue, by industry.
 *
 * A hand-checked list beats a paste box for anyone who does not already know
 * which publications cover their trade, which is most people. The group that
 * matches the business's own industry opens first and says so; the rest are
 * collapsed, because ten open groups is a wall, not a choice.
 *
 * A feed already followed is checked and disabled rather than hidden: the
 * answer to "is this one already in?" is worth more on screen than the space it
 * costs. Following a batch is one POST and no model call, so nothing here
 * spends a generation.
 */
export function NewsCatalogPanel({
  businessId,
  collection,
  industry,
  onAdded,
}: NewsCatalogPanelProps) {
  const catalog = useFeedCatalog();
  const [openState, setOpenState] = useState<{ slug: string | null } | null>(null);
  const [selectedIds, setSelectedIds] = useState<readonly string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [writeError, setWriteError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);

  const followed = useMemo(() => followedCatalogIds(collection.items), [collection.items]);

  const matchedSlug = useMemo(
    () => matchIndustryGroup(catalog.groups, industry),
    [catalog.groups, industry],
  );

  // Until the user opens one themselves, the open group is the one that matches
  // their trade, falling back to the first. After that their choice stands,
  // including their choice to have none open at all.
  const openSlug = openState ? openState.slug : (matchedSlug ?? catalog.groups[0]?.slug ?? null);

  function toggleGroup(slug: string) {
    setOpenState({ slug: openSlug === slug ? null : slug });
  }

  function isFollowed(feed: CuratedFeedRead): boolean {
    return followed.ids.has(feed.id) || followed.urls.has(normaliseFeedUrl(feed.feed_url));
  }

  function toggleFeed(id: string) {
    setReceipt(null);
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    );
  }

  // A feed followed by another tab while this one was open is no longer a
  // selection anybody can act on, so it never reaches the request.
  const addableIds = useMemo(() => {
    const byId = new Map(catalog.feeds.map((feed) => [feed.id, feed]));
    return selectedIds.filter((id) => {
      const feed = byId.get(id);
      if (feed === undefined) return false;
      return !followed.ids.has(feed.id) && !followed.urls.has(normaliseFeedUrl(feed.feed_url));
    });
  }, [selectedIds, catalog.feeds, followed]);

  async function addSelected() {
    if (addableIds.length === 0) return;

    setIsSaving(true);
    setWriteError(null);
    setReceipt(null);
    try {
      const created = await addFromCatalog(businessId, addableIds);
      collection.insertMany(created);
      setSelectedIds([]);
      setReceipt(
        created.length === addableIds.length
          ? `Following ${created.length} more ${created.length === 1 ? 'source' : 'sources'}. They join the next update.`
          : `Following ${created.length} more. The rest were already on your list.`,
      );
      onAdded();
    } catch (caught) {
      setWriteError(
        caught instanceof ApiError && caught.code === 'CATALOG_FEED_NOT_FOUND'
          ? 'One of those publications has left our list. Reload the list and pick again.'
          : getErrorMessage(caught, 'Those sources could not be added.'),
      );
    } finally {
      setIsSaving(false);
    }
  }

  if (catalog.isLoading) {
    return (
      <p className="kb-section__status" role="status">
        Loading our list
      </p>
    );
  }

  if (catalog.error) {
    return (
      <div className="feed-catalog">
        <p className="form__error" role="alert">
          {catalog.error}
        </p>
        <button className="button" type="button" onClick={catalog.reload}>
          Try again
        </button>
      </div>
    );
  }

  if (catalog.groups.length === 0) {
    return (
      <div className="kb-section__empty">
        <p className="kb-section__empty-text">
          Our list is empty just now. Paste a site of your own on the first tab.
        </p>
      </div>
    );
  }

  return (
    <div className="feed-catalog">
      <p className="field__hint">
        Publications we have checked, by trade. Pick as many as you like — following one costs
        nothing.
      </p>

      <ul className="feed-groups">
        {catalog.groups.map((group) => {
          const isOpen = group.slug === openSlug;
          const isOwn = group.slug === matchedSlug;
          const panelId = `feed-group-${group.slug}`;

          return (
            <li className="feed-group" key={group.slug}>
              <button
                className="feed-group__head"
                type="button"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => toggleGroup(group.slug)}
              >
                <span className="feed-group__name">{group.label}</span>
                {isOwn ? <span className="badge badge--neutral">{OWN_INDUSTRY_TAG}</span> : null}
                <span className="feed-group__count">{group.feeds.length}</span>
                <span className="feed-group__caret" aria-hidden="true">
                  {isOpen ? '–' : '+'}
                </span>
              </button>

              {isOpen ? (
                <ul className="feed-picks" id={panelId}>
                  {group.feeds.map((feed) => {
                    const already = isFollowed(feed);
                    const domain = domainOf(feed.site_url ?? feed.feed_url);

                    return (
                      <li key={feed.id}>
                        <label className={already ? 'feed-pick feed-pick--on' : 'feed-pick'}>
                          <input
                            type="checkbox"
                            className="feed-pick__box"
                            checked={already || selectedIds.includes(feed.id)}
                            disabled={already || isSaving}
                            onChange={() => toggleFeed(feed.id)}
                          />
                          <span className="feed-pick__body">
                            <span className="feed-pick__title">{feed.title}</span>
                            {feed.description ? (
                              <span className="feed-pick__note">{feed.description}</span>
                            ) : null}
                            {domain ? <span className="feed-pick__domain">{domain}</span> : null}
                          </span>
                          {already ? <span className="badge badge--active">Following</span> : null}
                        </label>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>

      {writeError ? (
        <p className="form__error" role="alert">
          {writeError}
        </p>
      ) : null}

      {receipt && !writeError ? (
        <p className="news-add__receipt" role="status">
          {receipt}
        </p>
      ) : null}

      <div className="feed-catalog__foot">
        <button
          className="button button--primary"
          type="button"
          disabled={addableIds.length === 0 || isSaving}
          onClick={() => void addSelected()}
        >
          {isSaving ? 'Adding' : `Add ${addableIds.length} selected`}
        </button>
      </div>
    </div>
  );
}
